// Typed fetch client and wire types for the validator-finances API.
//
// Contract highlights (docs/agent-api-skill.md of the API project):
// - null means unknown/unavailable, 0 means measured zero. Parsing preserves
//   null and never coerces to 0.
// - Big integers that can exceed 2^53 arrive as JSON strings
//   (activated_stake, cluster_stake, jitosol_pool_*). They are kept as
//   strings here and only ever handled with BigInt, never Number.
// - /apy, /cluster, /income, /rank respond with a bare array of row objects.
// - Errors respond {"error": "<reason>"} with HTTP 400/404/500.

export const DEFAULT_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  readonly status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/** Row of GET /apy (one per closed epoch). */
export interface ApyRow {
  epoch: number;
  duration_secs: number | null;
  net_total_lamports: number | null;
  bam_sol_lamports: number | null;
  vote_cost: number | null;
  operator_tx_cost: number | null;
  tips_gross: number | null;
  /** Big integer as decimal string; null when the epoch has no stake capture (permanent). */
  activated_stake: string | null;
  apr_stake_pct: number | null;
  apy_stake_pct: number | null;
  delegator_apr_pct: number | null;
  delegator_apy_pct: number | null;
  /**
   * Base (inflation-only) APR/APY: excludes MEV. Null both when the
   * inflation-only breakdown is unavailable on older API versions (see
   * ClusterData.inflationKeysPresent) and when the existing delegator
   * columns above are null for inflation reasons on a served row.
   */
  delegator_apr_inflation_pct: number | null;
  delegator_apy_inflation_pct: number | null;
}

/** Row of GET /cluster (one per epoch). */
export interface ClusterRow {
  epoch: number;
  validators: number | null;
  /** Big integer as decimal string. */
  cluster_stake: string | null;
  cluster_avg_delegator_apr_pct: number | null;
  cluster_stake_weighted_apr_pct: number | null;
  cluster_mev_apr_pct: number | null;
  cluster_total_apr_pct: number | null;
  commission_measured: number | null;
  commission_missing: number | null;
  // Distribution columns (net, incl. MEV). Older API versions serve rows
  // without these keys; see ClusterData.distributionKeysPresent.
  delegator_apy_p50_pct: number | null;
  delegator_apy_p90_pct: number | null;
  delegator_apy_max_pct: number | null;
  distribution_validators: number | null;
  // Base (inflation-only) distribution columns: need no stake capture, so
  // may carry a full-cluster distribution on epochs the net side gates.
  // Older/pre-0.7.0 API versions serve rows without these keys; see
  // ClusterData.inflationKeysPresent.
  delegator_apy_infl_p50_pct: number | null;
  delegator_apy_infl_p90_pct: number | null;
  delegator_apy_infl_max_pct: number | null;
  distribution_infl_validators: number | null;
  /**
   * Network AVERAGE (mean) base APY (schema v36). The true mean of the
   * per-validator base APY distribution — sits below the median. Plain
   * nullable field (no presence flag): null on API versions/epochs that
   * predate the column, same tolerance as every other optional /cluster
   * addition. The position line prefers it ("+X% above cluster average") and
   * falls back to the median when it is null.
   */
  delegator_apy_infl_avg_pct: number | null;
  /**
   * This validator's standing inside the epoch's measured BASE distribution:
   * share of that distribution strictly below our rate (0-100), and the
   * matching 1-based rank. Not yet deployed on every API version; both are
   * plain nullable fields (no presence flag) — null on rows that predate the
   * column, same tolerance as every other optional /cluster addition.
   */
  our_base_percentile_pct: number | null;
  our_base_rank: number | null;
}

export interface ClusterData {
  rows: ClusterRow[];
  /**
   * True when at least one row carries any of the net distribution keys
   * (delegator_apy_p50_pct / _p90_pct / _max_pct / distribution_validators).
   * False on older API versions; the widget then omits the median line and
   * band and shows a "network distribution pending" note.
   */
  distributionKeysPresent: boolean;
  /**
   * True when at least one row carries any of the base (inflation-only)
   * distribution keys (delegator_apy_infl_p50_pct / _p90_pct / _max_pct /
   * distribution_infl_validators). False on pre-0.7.0 API versions; the
   * widget then keeps the net-based rendering unchanged.
   */
  inflationKeysPresent: boolean;
}

/** Row of GET /income (raw epoch ledger, one per closed epoch). */
export interface IncomeRow {
  epoch: number;
  start_slot: number | null;
  start_time: number | null;
  end_time: number | null;
  block_fees_lamports: number | null;
  tips_gross_lamports: number | null;
  tips_gross_kobe_lamports: number | null;
  mev_claimed_lamports: number | null;
  inflation_commission_lamports: number | null;
  bam_boost_entitled_jitosol: number | null;
  /** Big integer as decimal string. */
  jitosol_pool_lamports: string | null;
  /** Big integer as decimal string. */
  jitosol_pool_supply: string | null;
  vote_cost_lamports: number | null;
  operator_tx_cost_lamports: number | null;
  mev_commission_bps: number | null;
  // USD valuation fields (daemon 0.12+). All three are absent (undefined,
  // key not on the wire row) on older daemons — presence gates the income
  // view's SOL|USD unit switch; tolerant of absence, strict when present.
  /** Blocks stored for the epoch (denominator for USD coverage). */
  blocks?: number | null;
  /** Blocks carrying an earn-time price; block_fees_usd_micro covers exactly these. */
  priced_blocks?: number | null;
  /**
   * Receipt-time valuation of the epoch's block fees, integer micro-USD:
   * each block's fee valued at that block's own earn-time SOL/USD price,
   * summed. Null when the epoch has zero priced blocks. A non-safe-integer
   * wire value (> 2^53, i.e. above ~$9e9 per epoch) parses to null —
   * precision-or-drop, never a rounded Number served as exact.
   */
  block_fees_usd_micro?: number | null;
}

/**
 * Row of GET /rank (vote-credit standing, one per epoch).
 *
 * `rank` is 1-based competition ranking (1 = best); only the fields the
 * rank chip and its top% derivation need are parsed (docs/agent-api-skill.md
 * §3.10 also serves top_vote_pubkey, unused here).
 */
export interface RankRow {
  epoch: number;
  credits_earned: number | null;
  rank: number | null;
  validators: number | null;
  percentile: number | null;
  top_credits: number | null;
  gap_to_top: number | null;
  behind_top_pct: number | null;
}

/** Head of GET /schedule: the daemon's observed chain position. */
export interface ScheduleHead {
  epoch: number;
  observed_slot: number;
  slot_index: number;
  slots_in_epoch: number;
  /**
   * Seconds since the daemon last observed the chain head; null when the
   * field is absent or unusable. Beyond ~300 s the schedule is stale
   * (contract §3.6) — consumers must disclose, not count down.
   */
  age_secs: number | null;
}

/**
 * Next scheduled leader slot. eta_secs is null when the served value is
 * unusable — the slot exists but its ETA is unknown, which is distinct
 * from next: null (nothing scheduled ahead this epoch).
 */
export interface ScheduleNext {
  eta_secs: number | null;
}

/**
 * Crawl/production status of one leader slot (contract §3.6): produced and
 * skipped are on-chain outcomes; scheduled, unavailable, and abandoned are
 * the engine's crawl lifecycle for a slot it has not (yet) fetched.
 */
export type ScheduleSlotStatus =
  | "produced"
  | "scheduled"
  | "skipped"
  | "unavailable"
  | "abandoned";

const SCHEDULE_SLOT_STATUSES: readonly string[] = [
  "produced",
  "scheduled",
  "skipped",
  "unavailable",
  "abandoned",
];

/** One leader slot of the `/schedule?epoch=N` timeline. */
export interface ScheduleSlot {
  slot: number;
  epoch: number;
  status: ScheduleSlotStatus;
  block_time: number | null;
  fee_reward_lamports: number | null;
}

/**
 * GET /schedule (plain object). Only the fields the live strip, leader
 * track, and blocks datastream read.
 */
export interface ScheduleData {
  head: ScheduleHead;
  /** null = no upcoming leader slot (contract: "next: null = nothing scheduled ahead"). */
  next: ScheduleNext | null;
  /** The requested epoch's full leader timeline; empty without `?epoch=N`. */
  slots: ScheduleSlot[];
}

/**
 * Row of GET /skips (per-epoch production quality, one per epoch).
 *
 * skip_rate_pct is null until the epoch has resolved slots (contract
 * §3.7); unavailable/abandoned are unresolved crawl states, not confirmed
 * misses.
 */
export interface SkipsRow {
  epoch: number;
  total: number | null;
  pending: number | null;
  produced: number | null;
  skipped: number | null;
  unavailable: number | null;
  abandoned: number | null;
  skip_rate_pct: number | null;
}

/**
 * GET /summary totals for one slot range (the live strip's so-far facts).
 *
 * /summary's `rows` is a single totals object, not an array (contract
 * §3.3). Fields are integer lamport/count sums; a non-safe-integer wire
 * value parses to null — precision-or-drop.
 */
export interface EpochSummary {
  blocks: number | null;
  fee_reward_lamports: number | null;
  tips_lamports: number | null;
}

// --- null-safe field readers -------------------------------------------------

function numOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/**
 * Integer counter/amount field: safe-integer numbers pass, anything else
 * (including floats and values beyond 2^53) is null — precision-or-drop.
 */
function safeIntOrNull(v: unknown): number | null {
  return typeof v === "number" && Number.isSafeInteger(v) ? v : null;
}

/**
 * Big-integer field: keep the wire string; a safe-integer number is
 * stringified for uniform BigInt handling. Anything else is null.
 */
function bigIntStringOrNull(v: unknown): string | null {
  if (typeof v === "string" && /^-?[0-9]+$/.test(v)) {
    return v;
  }
  if (typeof v === "number" && Number.isSafeInteger(v)) {
    return String(v);
  }
  return null;
}

function asRowObject(v: unknown, ctx: string, index: number): Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new ApiError(`${ctx}: row ${index} is not an object`);
  }
  return v as Record<string, unknown>;
}

function asRowArray(v: unknown, ctx: string): unknown[] {
  if (!Array.isArray(v)) {
    throw new ApiError(`${ctx}: expected a bare JSON array of rows`);
  }
  return v;
}

/** Epoch is the join key across endpoints; a row without it cannot be aligned. */
function requireEpoch(row: Record<string, unknown>, ctx: string, index: number): number {
  const e = row["epoch"];
  if (typeof e !== "number" || !Number.isSafeInteger(e) || e < 0) {
    throw new ApiError(`${ctx}: row ${index} has no integer "epoch" field`);
  }
  return e;
}

// --- per-endpoint parsers (pure, unit-tested) --------------------------------

export function parseApyRows(json: unknown): ApyRow[] {
  const ctx = "/apy";
  return asRowArray(json, ctx).map((raw, i) => {
    const r = asRowObject(raw, ctx, i);
    return {
      epoch: requireEpoch(r, ctx, i),
      duration_secs: numOrNull(r["duration_secs"]),
      net_total_lamports: numOrNull(r["net_total_lamports"]),
      bam_sol_lamports: numOrNull(r["bam_sol_lamports"]),
      vote_cost: numOrNull(r["vote_cost"]),
      operator_tx_cost: numOrNull(r["operator_tx_cost"]),
      tips_gross: numOrNull(r["tips_gross"]),
      activated_stake: bigIntStringOrNull(r["activated_stake"]),
      apr_stake_pct: numOrNull(r["apr_stake_pct"]),
      apy_stake_pct: numOrNull(r["apy_stake_pct"]),
      delegator_apr_pct: numOrNull(r["delegator_apr_pct"]),
      delegator_apy_pct: numOrNull(r["delegator_apy_pct"]),
      delegator_apr_inflation_pct: numOrNull(r["delegator_apr_inflation_pct"]),
      delegator_apy_inflation_pct: numOrNull(r["delegator_apy_inflation_pct"]),
    };
  });
}

const DISTRIBUTION_KEYS = [
  "delegator_apy_p50_pct",
  "delegator_apy_p90_pct",
  "delegator_apy_max_pct",
  "distribution_validators",
] as const;

const INFLATION_DISTRIBUTION_KEYS = [
  "delegator_apy_infl_p50_pct",
  "delegator_apy_infl_p90_pct",
  "delegator_apy_infl_max_pct",
  "distribution_infl_validators",
] as const;

export function parseClusterRows(json: unknown): ClusterData {
  const ctx = "/cluster";
  let distributionKeysPresent = false;
  let inflationKeysPresent = false;
  const rows = asRowArray(json, ctx).map((raw, i) => {
    const r = asRowObject(raw, ctx, i);
    if (DISTRIBUTION_KEYS.some((k) => k in r)) {
      distributionKeysPresent = true;
    }
    if (INFLATION_DISTRIBUTION_KEYS.some((k) => k in r)) {
      inflationKeysPresent = true;
    }
    return {
      epoch: requireEpoch(r, ctx, i),
      validators: numOrNull(r["validators"]),
      cluster_stake: bigIntStringOrNull(r["cluster_stake"]),
      cluster_avg_delegator_apr_pct: numOrNull(r["cluster_avg_delegator_apr_pct"]),
      cluster_stake_weighted_apr_pct: numOrNull(r["cluster_stake_weighted_apr_pct"]),
      cluster_mev_apr_pct: numOrNull(r["cluster_mev_apr_pct"]),
      cluster_total_apr_pct: numOrNull(r["cluster_total_apr_pct"]),
      commission_measured: numOrNull(r["commission_measured"]),
      commission_missing: numOrNull(r["commission_missing"]),
      delegator_apy_p50_pct: numOrNull(r["delegator_apy_p50_pct"]),
      delegator_apy_p90_pct: numOrNull(r["delegator_apy_p90_pct"]),
      delegator_apy_max_pct: numOrNull(r["delegator_apy_max_pct"]),
      distribution_validators: numOrNull(r["distribution_validators"]),
      delegator_apy_infl_p50_pct: numOrNull(r["delegator_apy_infl_p50_pct"]),
      delegator_apy_infl_p90_pct: numOrNull(r["delegator_apy_infl_p90_pct"]),
      delegator_apy_infl_max_pct: numOrNull(r["delegator_apy_infl_max_pct"]),
      distribution_infl_validators: numOrNull(r["distribution_infl_validators"]),
      delegator_apy_infl_avg_pct: numOrNull(r["delegator_apy_infl_avg_pct"]),
      our_base_percentile_pct: numOrNull(r["our_base_percentile_pct"]),
      our_base_rank: numOrNull(r["our_base_rank"]),
    };
  });
  return { rows, distributionKeysPresent, inflationKeysPresent };
}

export function parseIncomeRows(json: unknown): IncomeRow[] {
  const ctx = "/income";
  return asRowArray(json, ctx).map((raw, i) => {
    const r = asRowObject(raw, ctx, i);
    const row: IncomeRow = {
      epoch: requireEpoch(r, ctx, i),
      start_slot: numOrNull(r["start_slot"]),
      start_time: numOrNull(r["start_time"]),
      end_time: numOrNull(r["end_time"]),
      block_fees_lamports: numOrNull(r["block_fees_lamports"]),
      tips_gross_lamports: numOrNull(r["tips_gross_lamports"]),
      tips_gross_kobe_lamports: numOrNull(r["tips_gross_kobe_lamports"]),
      mev_claimed_lamports: numOrNull(r["mev_claimed_lamports"]),
      inflation_commission_lamports: numOrNull(r["inflation_commission_lamports"]),
      bam_boost_entitled_jitosol: numOrNull(r["bam_boost_entitled_jitosol"]),
      jitosol_pool_lamports: bigIntStringOrNull(r["jitosol_pool_lamports"]),
      jitosol_pool_supply: bigIntStringOrNull(r["jitosol_pool_supply"]),
      vote_cost_lamports: numOrNull(r["vote_cost_lamports"]),
      operator_tx_cost_lamports: numOrNull(r["operator_tx_cost_lamports"]),
      mev_commission_bps: numOrNull(r["mev_commission_bps"]),
    };
    // USD valuation fields keep absent-vs-null apart (tx-decode precedent):
    // a key not on the wire row stays undefined (old daemon — the unit
    // switch never renders); a served key is parsed strictly.
    if ("blocks" in r) {
      row.blocks = safeIntOrNull(r["blocks"]);
    }
    if ("priced_blocks" in r) {
      row.priced_blocks = safeIntOrNull(r["priced_blocks"]);
    }
    if ("block_fees_usd_micro" in r) {
      row.block_fees_usd_micro = safeIntOrNull(r["block_fees_usd_micro"]);
    }
    return row;
  });
}

export function parseRankRows(json: unknown): RankRow[] {
  const ctx = "/rank";
  return asRowArray(json, ctx).map((raw, i) => {
    const r = asRowObject(raw, ctx, i);
    return {
      epoch: requireEpoch(r, ctx, i),
      credits_earned: numOrNull(r["credits_earned"]),
      rank: numOrNull(r["rank"]),
      validators: numOrNull(r["validators"]),
      percentile: numOrNull(r["percentile"]),
      top_credits: numOrNull(r["top_credits"]),
      gap_to_top: numOrNull(r["gap_to_top"]),
      behind_top_pct: numOrNull(r["behind_top_pct"]),
    };
  });
}

function asPlainObject(v: unknown, ctx: string, what: string): Record<string, unknown> {
  if (typeof v !== "object" || v === null || Array.isArray(v)) {
    throw new ApiError(`${ctx}: ${what} is not an object`);
  }
  return v as Record<string, unknown>;
}

export function parseSchedule(json: unknown): ScheduleData {
  const ctx = "/schedule";
  const o = asPlainObject(json, ctx, "response body");
  const h = asPlainObject(o["head"], ctx, '"head"');
  const requireIndexField = (key: string): number => {
    const v = h[key];
    if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) {
      throw new ApiError(`${ctx}: head.${key} is not a non-negative integer`);
    }
    return v;
  };
  const slotsInEpoch = requireIndexField("slots_in_epoch");
  if (slotsInEpoch < 1) {
    // Denominator of epoch progress; zero would make the strip divide by it.
    throw new ApiError(`${ctx}: head.slots_in_epoch must be positive`);
  }
  const head: ScheduleHead = {
    epoch: requireIndexField("epoch"),
    observed_slot: requireIndexField("observed_slot"),
    slot_index: requireIndexField("slot_index"),
    slots_in_epoch: slotsInEpoch,
    age_secs: numOrNull(h["age_secs"]),
  };
  const rawNext = o["next"];
  let next: ScheduleNext | null = null;
  if (rawNext !== null && rawNext !== undefined) {
    // Present-but-malformed is an envelope error, not a silent "no slot":
    // rendering "no further leader slots" off garbage would be a false
    // statement about the schedule.
    const n = asPlainObject(rawNext, ctx, '"next"');
    next = { eta_secs: safeIntOrNull(n["eta_secs"]) };
  }
  // Timeline rows (served with ?epoch=N; [] otherwise). Strict when
  // present, like `next`: a malformed row — including a status string this
  // client cannot truthfully categorize — fails the poll (the consumer
  // keeps its last facts) rather than rendering a guess.
  const rawSlots = o["slots"] ?? [];
  if (!Array.isArray(rawSlots)) {
    throw new ApiError(`${ctx}: "slots" is not an array`);
  }
  const slots: ScheduleSlot[] = rawSlots.map((raw, i) => {
    const r = asRowObject(raw, `${ctx} slots`, i);
    const requireSlotInt = (key: string): number => {
      const v = r[key];
      if (typeof v !== "number" || !Number.isSafeInteger(v) || v < 0) {
        throw new ApiError(`${ctx}: slots row ${i} "${key}" is not a non-negative integer`);
      }
      return v;
    };
    const status = r["status"];
    if (typeof status !== "string" || !SCHEDULE_SLOT_STATUSES.includes(status)) {
      throw new ApiError(`${ctx}: slots row ${i} has an unknown status`);
    }
    return {
      slot: requireSlotInt("slot"),
      epoch: requireSlotInt("epoch"),
      status: status as ScheduleSlotStatus,
      block_time: safeIntOrNull(r["block_time"]),
      fee_reward_lamports: safeIntOrNull(r["fee_reward_lamports"]),
    };
  });
  return { head, next, slots };
}

export function parseSkipsRows(json: unknown): SkipsRow[] {
  const ctx = "/skips";
  return asRowArray(json, ctx).map((raw, i) => {
    const r = asRowObject(raw, ctx, i);
    return {
      epoch: requireEpoch(r, ctx, i),
      total: safeIntOrNull(r["total"]),
      pending: safeIntOrNull(r["pending"]),
      produced: safeIntOrNull(r["produced"]),
      skipped: safeIntOrNull(r["skipped"]),
      unavailable: safeIntOrNull(r["unavailable"]),
      abandoned: safeIntOrNull(r["abandoned"]),
      skip_rate_pct: numOrNull(r["skip_rate_pct"]),
    };
  });
}

export function parseEpochSummary(json: unknown): EpochSummary {
  const ctx = "/summary";
  const o = asPlainObject(json, ctx, "response body");
  const rows = asPlainObject(o["rows"], ctx, '"rows" totals');
  return {
    blocks: safeIntOrNull(rows["blocks"]),
    fee_reward_lamports: safeIntOrNull(rows["fee_reward_lamports"]),
    tips_lamports: safeIntOrNull(rows["tips_lamports"]),
  };
}

// --- HTTP client --------------------------------------------------------------

type QueryParams = Record<string, number | undefined>;

async function getJson(
  base: string,
  path: string,
  params: QueryParams,
  timeoutMs: number,
  signal?: AbortSignal,
): Promise<unknown> {
  let url: URL;
  try {
    // Concatenate instead of new URL(path, base) so a base with a subpath
    // ("https://example.com/vf") is preserved.
    url = new URL(base.replace(/\/+$/, "") + path);
  } catch {
    throw new ApiError(`invalid api-base URL: "${base}"`);
  }
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined) {
      url.searchParams.set(k, String(v));
    }
  }

  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), timeoutMs);
  const onOuterAbort = (): void => ctl.abort();
  signal?.addEventListener("abort", onOuterAbort, { once: true });
  if (signal?.aborted) {
    ctl.abort();
  }

  try {
    let res: Response;
    try {
      res = await fetch(url, {
        signal: ctl.signal,
        headers: { accept: "application/json" },
      });
    } catch (e) {
      if (signal?.aborted) {
        throw new ApiError(`${path}: request aborted`);
      }
      if (ctl.signal.aborted) {
        throw new ApiError(`${path}: timeout after ${timeoutMs} ms`);
      }
      throw new ApiError(`${path}: network error (${e instanceof Error ? e.message : String(e)})`);
    }
    if (!res.ok) {
      let detail = "";
      try {
        const body: unknown = await res.json();
        if (typeof body === "object" && body !== null && "error" in body) {
          const msg = (body as Record<string, unknown>)["error"];
          if (typeof msg === "string") {
            detail = `: ${msg}`;
          }
        }
      } catch {
        // Non-JSON error body; the status code is the message.
      }
      throw new ApiError(`${path}: HTTP ${res.status}${detail}`, res.status);
    }
    try {
      return await res.json();
    } catch {
      throw new ApiError(`${path}: response is not valid JSON`);
    }
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onOuterAbort);
  }
}

export interface ApiClient {
  fetchApy(signal?: AbortSignal): Promise<ApyRow[]>;
  fetchCluster(fromEpoch?: number, toEpoch?: number, signal?: AbortSignal): Promise<ClusterData>;
  fetchIncome(signal?: AbortSignal): Promise<IncomeRow[]>;
  fetchRank(signal?: AbortSignal): Promise<RankRow[]>;
  /** With `epoch`, the response carries that epoch's full `slots` timeline. */
  fetchSchedule(signal?: AbortSignal, epoch?: number): Promise<ScheduleData>;
  fetchEpochSummary(fromSlot: number, toSlot: number, signal?: AbortSignal): Promise<EpochSummary>;
  fetchSkips(fromEpoch?: number, signal?: AbortSignal): Promise<SkipsRow[]>;
}

/**
 * Client for one API origin.
 *
 * from_epoch/to_epoch are sent only to /cluster (the endpoint that documents
 * them); /apy, /income, and /rank are filtered client-side in series
 * assembly, since the API rejects unknown query params with HTTP 400.
 */
export function createClient(base: string, timeoutMs: number = DEFAULT_TIMEOUT_MS): ApiClient {
  return {
    async fetchApy(signal?: AbortSignal): Promise<ApyRow[]> {
      return parseApyRows(await getJson(base, "/apy", {}, timeoutMs, signal));
    },
    async fetchCluster(
      fromEpoch?: number,
      toEpoch?: number,
      signal?: AbortSignal,
    ): Promise<ClusterData> {
      return parseClusterRows(
        await getJson(
          base,
          "/cluster",
          { from_epoch: fromEpoch, to_epoch: toEpoch },
          timeoutMs,
          signal,
        ),
      );
    },
    async fetchIncome(signal?: AbortSignal): Promise<IncomeRow[]> {
      return parseIncomeRows(await getJson(base, "/income", {}, timeoutMs, signal));
    },
    async fetchRank(signal?: AbortSignal): Promise<RankRow[]> {
      return parseRankRows(await getJson(base, "/rank", {}, timeoutMs, signal));
    },
    async fetchSchedule(signal?: AbortSignal, epoch?: number): Promise<ScheduleData> {
      return parseSchedule(await getJson(base, "/schedule", { epoch }, timeoutMs, signal));
    },
    async fetchEpochSummary(
      fromSlot: number,
      toSlot: number,
      signal?: AbortSignal,
    ): Promise<EpochSummary> {
      return parseEpochSummary(
        await getJson(base, "/summary", { from_slot: fromSlot, to_slot: toSlot }, timeoutMs, signal),
      );
    },
    async fetchSkips(fromEpoch?: number, signal?: AbortSignal): Promise<SkipsRow[]> {
      return parseSkipsRows(
        await getJson(base, "/skips", { from_epoch: fromEpoch }, timeoutMs, signal),
      );
    },
  };
}
